// src/components/home/HomePage.tsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Avatar,
} from '@mui/material';
import {
  VideoLibrary,
  Psychology,
  Handshake,
  Search,
  Send,
  CheckCircle,
} from '@mui/icons-material';
import { useUser } from '../context/UserContext';
import { useAuth0User } from '../context/Auth0UserContext';

const useAuth0 = process.env.REACT_APP_USE_AUTH0 === 'true';

function Auth0HomePage() {
  const { user } = useAuth0User();
  return <HomePageContent user={user} />;
}

function OriginalHomePage() {
  const { user } = useUser();
  return <HomePageContent user={user} />;
}

const HomePageContent: React.FC<{ user: any }> = ({ user }) => {
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          pt: { xs: 8, sm: 10, md: 12 },
          pb: { xs: 6, sm: 8, md: 10 },
          color: 'white',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' }, gap: { xs: 3, md: 6 }, alignItems: 'center' }}>
            <Box>
              <Typography
                variant="h2"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
                  mb: { xs: 1.5, md: 2 },
                  lineHeight: { xs: 1.1, md: 1.2 }
                }}
              >
                Get On Stage. Get Heard. Get Booked.
              </Typography>

              <Typography
                variant="h5"
                sx={{
                  fontWeight: 400,
                  opacity: 0.9,
                  mb: { xs: 3, md: 4 },
                  fontSize: { xs: '1.1rem', sm: '1.25rem', md: '1.5rem' }
                }}
              >
                Find open spots on podcasts, live shows, and studio sessions. Apply in minutes, get booked, build your track record.
              </Typography>

              <Box sx={{ display: 'flex', gap: { xs: 1.5, md: 2 }, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', md: 'flex-start' } }}>
                {user ? (
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/explore')}
                    sx={{
                      bgcolor: 'white',
                      color: 'primary.main',
                      px: { xs: 3, md: 4 },
                      py: { xs: 1.25, md: 1.5 },
                      fontWeight: 600,
                      minHeight: '48px',
                      fontSize: { xs: '0.95rem', md: '1rem' },
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.9)',
                      },
                    }}
                  >
                    See What's Open
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="contained"
                      size="large"
                      onClick={() => navigate('/explore')}
                      sx={{
                        bgcolor: 'white',
                        color: 'primary.main',
                        px: { xs: 3, md: 4 },
                        py: { xs: 1.25, md: 1.5 },
                        fontWeight: 600,
                        minHeight: '48px',
                        fontSize: { xs: '0.95rem', md: '1rem' },
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.9)',
                        },
                      }}
                    >
                      Find Your Spot
                    </Button>
                    <Button
                      variant="outlined"
                      size="large"
                      onClick={() => navigate('/explore')}
                      sx={{
                        borderColor: 'rgba(255,255,255,0.5)',
                        color: 'white',
                        px: { xs: 3, md: 4 },
                        py: { xs: 1.25, md: 1.5 },
                        fontWeight: 600,
                        minHeight: '48px',
                        fontSize: { xs: '0.95rem', md: '1rem' },
                        '&:hover': {
                          borderColor: 'white',
                          bgcolor: 'rgba(255,255,255,0.1)',
                        },
                      }}
                    >
                      See What's Open
                    </Button>
                  </>
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'block', mt: { xs: 3, md: 0 } }}>
              <Box
                component="img"
                src="https://images.unsplash.com/photo-1590602847861-f357a9332bbc?w=1000&h=700&fit=crop&crop=center&q=80"
                alt="Creators collaborating in a podcast studio"
                sx={{
                  width: '100%',
                  maxWidth: { xs: 360, sm: 420, md: 520 },
                  height: { xs: 220, sm: 280, md: 380 },
                  objectFit: 'cover',
                  objectPosition: 'center',
                  display: 'block',
                  mx: { xs: 'auto', md: 0 },
                  borderRadius: 3,
                  boxShadow: '0 15px 30px rgba(0,0,0,0.2)'
                }}
              />
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Features Section - Hosts */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Typography
          variant="h3"
          align="center"
          sx={{
            fontWeight: 700,
            mb: { xs: 4, md: 6 },
            fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' }
          }}
        >
          Find the Right Guests, Fast
        </Typography>

        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)'
            },
            gap: 4
          }}
        >
          {[
            {
              icon: VideoLibrary,
              title: "Set Up Your Shoot",
              description: "Create a shoot for your podcast, livestream, or video series. Define your guest spots and let the right talent come to you.",
              color: '#667EEA'
            },
            {
              icon: Psychology,
              title: "Review Requests",
              description: "Every request comes with an AI-powered compatibility score. Spend less time screening and more time creating.",
              color: '#F093FB'
            },
            {
              icon: Handshake,
              title: "Book & Create",
              description: "Lock in your guests, sync schedules, and make something worth watching. One-click confirmations, reliable creators.",
              color: '#4FACFE'
            }
          ].map((feature, index) => {
            const IconComponent = feature.icon;
            return (
            <Paper
              key={index}
              elevation={0}
              sx={{
                p: { xs: 3, sm: 3.5, md: 4 },
                height: '100%',
                borderRadius: 3,
                bgcolor: 'white',
                border: '1px solid rgba(0,0,0,0.08)',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  transform: 'translateY(-6px)',
                  borderColor: feature.color,
                  '& .number-badge': {
                    transform: 'scale(1.1) rotate(5deg)',
                  },
                  '& .icon-wrapper': {
                    transform: 'scale(1.05)',
                  }
                },
                transition: 'all 0.3s ease',
              }}
            >
              {/* Number Badge */}
              <Avatar
                className="number-badge"
                sx={{
                  position: 'absolute',
                  top: 16,
                  right: 16,
                  width: 40,
                  height: 40,
                  bgcolor: `${feature.color}15`,
                  color: feature.color,
                  fontWeight: 700,
                  fontSize: '1.25rem',
                  transition: 'transform 0.3s ease',
                }}
              >
                {index + 1}
              </Avatar>

              {/* Icon */}
              <Box
                className="icon-wrapper"
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 2,
                  bgcolor: `${feature.color}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2.5,
                  transition: 'transform 0.3s ease',
                }}
              >
                <IconComponent sx={{ fontSize: 32, color: feature.color }} />
              </Box>

              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, fontSize: { xs: '1.15rem', md: '1.3rem' }, pr: 6 }}>
                {feature.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.95rem', md: '1rem' }, lineHeight: 1.7 }}>
                {feature.description}
              </Typography>
            </Paper>
          )})}
        </Box>
      </Container>

      {/* Features Section - Guests */}
      <Box sx={{ bgcolor: 'rgba(45, 32, 112, 0.02)', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h3"
            align="center"
            sx={{
              fontWeight: 700,
              mb: { xs: 4, md: 6 },
              fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem' }
            }}
          >
            Get Featured on Shows You Respect
          </Typography>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, 1fr)',
                md: 'repeat(3, 1fr)'
              },
              gap: 4
            }}
          >
            {[
              {
                icon: Search,
                title: "Find Your Stage",
                description: "Browse live opportunities from creators and collectives running podcasts, livestreams, music sessions, and more.",
                color: '#11998E'
              },
              {
                icon: Send,
                title: "Make Your Pitch",
                description: "Request spots that match your expertise. Show creators why you're the right fit for their next episode, set, or session.",
                color: '#FA709A'
              },
              {
                icon: CheckCircle,
                title: "Get Booked",
                description: "Creators review your request, AI helps match the right fit, and you get notified when you're confirmed.",
                color: '#43E97B'
              }
            ].map((feature, index) => {
              const IconComponent = feature.icon;
              return (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  p: { xs: 3, sm: 3.5, md: 4 },
                  height: '100%',
                  borderRadius: 3,
                  bgcolor: 'white',
                  border: '1px solid rgba(0,0,0,0.08)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&:hover': {
                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                    transform: 'translateY(-6px)',
                    borderColor: feature.color,
                    '& .number-badge': {
                      transform: 'scale(1.1) rotate(5deg)',
                    },
                    '& .icon-wrapper': {
                      transform: 'scale(1.05)',
                    }
                  },
                  transition: 'all 0.3s ease',
                }}
              >
                {/* Number Badge */}
                <Avatar
                  className="number-badge"
                  sx={{
                    position: 'absolute',
                    top: 16,
                    right: 16,
                    width: 40,
                    height: 40,
                    bgcolor: `${feature.color}15`,
                    color: feature.color,
                    fontWeight: 700,
                    fontSize: '1.25rem',
                    transition: 'transform 0.3s ease',
                  }}
                >
                  {index + 1}
                </Avatar>

                {/* Icon */}
                <Box
                  className="icon-wrapper"
                  sx={{
                    width: 56,
                    height: 56,
                    borderRadius: 2,
                    bgcolor: `${feature.color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mb: 2.5,
                    transition: 'transform 0.3s ease',
                  }}
                >
                  <IconComponent sx={{ fontSize: 32, color: feature.color }} />
                </Box>

                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1.5, fontSize: { xs: '1.15rem', md: '1.3rem' }, pr: 6 }}>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.95rem', md: '1rem' }, lineHeight: 1.7 }}>
                  {feature.description}
                </Typography>
              </Paper>
            )})}
          </Box>
        </Container>
      </Box>

      {/* Call to Action */}
      <Box sx={{ bgcolor: 'rgba(45, 32, 112, 0.05)', py: { xs: 4, md: 8 } }}>
        <Container maxWidth="md">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, sm: 4, md: 5 },
              borderRadius: 3,
              textAlign: 'center',
              bgcolor: 'white'
            }}
          >
            <Typography variant="h4" sx={{ fontWeight: 700, mb: { xs: 1.5, md: 2 }, fontSize: { xs: '1.5rem', md: '2rem' } }}>
              Your Next Collab Starts Here
            </Typography>
            <Typography variant="body1" sx={{ mb: { xs: 3, md: 4 }, color: 'text.secondary', fontSize: { xs: '1rem', md: '1.1rem' } }}>
              Browse open spots on podcasts, shows, and live stages. Apply in minutes.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              {user ? (
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => navigate('/explore')}
                  sx={{
                    bgcolor: 'primary.main',
                    px: { xs: 3, md: 4 },
                    py: { xs: 1.25, md: 1.5 },
                    fontWeight: 600,
                    minHeight: '48px',
                    fontSize: { xs: '0.95rem', md: '1rem' },
                    '&:hover': {
                      bgcolor: 'primary.dark',
                    },
                  }}
                >
                  See What's Open
                </Button>
              ) : (
                <>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={() => navigate('/explore')}
                    sx={{
                      bgcolor: 'primary.main',
                      px: { xs: 3, md: 4 },
                      py: { xs: 1.25, md: 1.5 },
                      fontWeight: 600,
                      minHeight: '48px',
                      fontSize: { xs: '0.95rem', md: '1rem' },
                      '&:hover': {
                        bgcolor: 'primary.dark',
                      },
                    }}
                  >
                    Find Your Spot
                  </Button>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={() => navigate('/explore')}
                    sx={{
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      px: { xs: 3, md: 4 },
                      py: { xs: 1.25, md: 1.5 },
                      fontWeight: 600,
                      minHeight: '48px',
                      fontSize: { xs: '0.95rem', md: '1rem' },
                      '&:hover': {
                        borderColor: 'primary.dark',
                        bgcolor: 'rgba(45, 32, 112, 0.04)',
                      },
                    }}
                  >
                    See What's Open
                  </Button>
                </>
              )}
            </Box>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
};

const HomePage: React.FC = () => {
  return useAuth0 ? <Auth0HomePage /> : <OriginalHomePage />;
};

export default HomePage;
